import { useState } from "react";
import { FaEdit, FaTrash, FaEye, FaSearch, FaPlus } from "react-icons/fa";
import { useProducts } from "./ProductContext";
import { useTheme } from "./ThemeContext";
import "../ProductsPage.css";

function ProductsPage({ onEditProduct, onAddProductClick }) {
  const { products, deleteProduct } = useProducts();
  const { isDarkMode } = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);

  const filteredProducts = products.filter(product =>
    product.productName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = (id, productName) => {
    if (window.confirm(`Are you sure you want to delete "${productName}"?`)) {
      deleteProduct(id);
    }
  };

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
  };

  const closeModal = () => {
    setSelectedProduct(null);
  };

  return (
    <div className={`products-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="products-header">
        <div>
          <h2>Product Inventory</h2>
          <p className="total-products">Total: {filteredProducts.length} products</p>
        </div>
        <div className="products-actions">
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="add-product-btn" onClick={onAddProductClick}>
            <FaPlus /> Add Product
          </button>
        </div>
      </div>

      {filteredProducts.length === 0 ? (
        <div className="no-products">
          <div className="no-products-icon">📦</div>
          <h3>No Products Found</h3>
          <p>Click "Add Product" to get started!</p>
        </div>
      ) : (
        <div className="products-table-container">
          <table className="products-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Product Name</th>
                <th>Wattages</th>
                <th>Applications</th>
                <th>Technical Specs</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id}>
                  <td className="product-image-cell">
                    {product.images && product.images[0] ? (
                      <img 
                        src={product.images[0]} 
                        alt={product.productName}
                        className="product-thumbnail"
                      />
                    ) : (
                      <div className="no-image">No Image</div>
                    )}
                  </td>
                  <td className="product-name-cell">
                    <strong>{product.productName}</strong>
                    {product.description && (
                      <p className="product-description">{product.description.substring(0, 50)}...</p>
                    )}
                  </td>
                  <td>
                    <div className="wattages-list">
                      {product.wattages?.map((w, idx) => (
                        <span key={idx} className="wattage-badge">
                          {w.watt}: ₹{w.price}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <div className="applications-list">
                      {product.applications?.slice(0, 2).map((app, idx) => (
                        <span key={idx} className="app-badge">{app}</span>
                      ))}
                      {product.applications?.length > 2 && (
                        <span className="more-badge">+{product.applications.length - 2}</span>
                      )}
                    </div>
                   </td>
                  <td className="technical-specs-cell">
                    <div className="tech-specs-list">
                      {product.technicalSpecs?.slice(0, 2).map((spec, idx) => (
                        <span key={idx} className="tech-spec-badge">
                          {spec.key}: {spec.value}
                        </span>
                      ))}
                      {product.technicalSpecs?.length > 2 && (
                        <span className="more-badge">+{product.technicalSpecs.length - 2}</span>
                      )}
                    </div>
                   </td>
                  <td className="actions-cell">
                    <button 
                      className="action-btn view-btn"
                      onClick={() => handleViewDetails(product)}
                      title="View Details"
                    >
                      <FaEye />
                    </button>
                    <button 
                      className="action-btn edit-btn"
                      onClick={() => onEditProduct(product.id)}
                      title="Edit Product"
                    >
                      <FaEdit />
                    </button>
                    <button 
                      className="action-btn delete-btn"
                      onClick={() => handleDelete(product.id, product.productName)}
                      title="Delete Product"
                    >
                      <FaTrash />
                    </button>
                   </td>
                 </tr>
              ))}
            </tbody>
           </table>
        </div>
      )}

      {/* Product Details Modal */}
      {selectedProduct && (
        <div className={`modal-overlay ${isDarkMode ? 'dark-mode' : ''}`} onClick={closeModal}>
          <div className={`details-modal ${isDarkMode ? 'dark-mode' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="details-header">
              <h2>{selectedProduct.productName}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>
            <div className="details-content">
              {selectedProduct.images && selectedProduct.images.length > 0 && (
                <div className="details-images">
                  {selectedProduct.images.map((img, idx) => (
                    <img key={idx} src={img} alt={`${selectedProduct.productName} ${idx + 1}`} />
                  ))}
                </div>
              )}
              
              <div className="details-section">
                <h3>Description</h3>
                <p>{selectedProduct.description || "No description provided"}</p>
              </div>

              <div className="details-section">
                <h3>Wattages & Prices</h3>
                <ul>
                  {selectedProduct.wattages?.map((w, idx) => (
                    <li key={idx}>{w.watt} - ₹{w.price}</li>
                  ))}
                </ul>
              </div>

              <div className="details-section">
                <h3>Applications</h3>
                <ul>
                  {selectedProduct.applications?.map((app, idx) => (
                    <li key={idx}>{app}</li>
                  ))}
                </ul>
              </div>

              <div className="details-section">
                <h3>Technical Specifications</h3>
                <table className="specs-details-table">
                  <tbody>
                    {selectedProduct.technicalSpecs?.map((spec, idx) => (
                      <tr key={idx}>
                        <td className="spec-key">{spec.key}</td>
                        <td className="spec-value">{spec.value}</td>
                       </tr>
                    ))}
                  </tbody>
                 </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductsPage;